import { useQuery } from "react-query";
import { fetchApi } from "../helpers/reactQueryApi";
import type { Group } from "../@types";

/**
 * The fields both sources of group data reliably agree on:
 * - `Preload.my_groups` (`GroupPresenter#frontend_data`), available
 *   synchronously for groups the current member belongs to, and
 * - `GET /groups/:slug.json` (`groups/_group.json.ruby` + `membership_roles`
 *   and `main_action` from `groups/show.json.ruby`),
 *   fetched on-demand for any other group the user may access (e.g. a network
 *   admin managing a group they are not a member of).
 */
export type GroupData = Pick<
  Group,
  | "id"
  | "name"
  | "slug"
  | "externally_visible"
  | "membership_roles"
  | "main_action"
>;

/**
 * Resolve a group by slug, preferring the `Preload.my_groups` snapshot and
 * falling back to an on-demand fetch when the group is not preloaded.
 *
 * Replaces the legacy `Preload.current_group`, which was only emitted on full
 * (non-CORS) document loads and therefore went stale on client-side (SPA)
 * navigation.
 */
export function useGroup(groupSlug: string | null | undefined) {
  const preloaded = groupSlug
    ? Preload.my_groups?.find((group) => group.slug === groupSlug)
    : undefined;

  return useQuery<GroupData>(
    ["group", groupSlug],
    () => fetchApi(`/groups/${groupSlug}.json`),
    {
      enabled: !!groupSlug && !preloaded,
      initialData: preloaded,
      refetchOnWindowFocus: false,
    },
  );
}
